/**
 * Pi.js - Batches and Rendering Module
 *
 * Batch system for rendering points and images efficiently.
 * Combines batch management and rendering operations.
 *
 * @module api/renderer/batches
 */

"use strict";

import * as g_contextState from "./context-state.js";
import * as g_shaders from "./shaders.js";
import * as g_screenManager from "../core/screen-manager.js";
import * as g_blends from "../api/blends.js";

// Shaders are imported from external files via esbuild text loader
import m_pointVertSrc from "./shaders/point.vert";
import m_pointFragSrc from "./shaders/point.frag";
import m_imageVertSrc from "./shaders/image.vert";
import m_imageFragSrc from "./shaders/image.frag";
import m_gemoetryVertSrc from "./shaders/geometry.vert";


/**************************************************************************************************
 * Constants
 ************************************************************************************************/


export const POINTS_BATCH = 0;
export const IMAGE_BATCH = 1;
export const GEOMETRY_BATCH = 2;
export const POINTS_REPLACE_BATCH = 3;
export const IMAGE_REPLACE_BATCH = 4;
export const SHADER_BATCH = 5;

// Resize by doubling up to 8 times
const MAX_SIZE_MULTIPLIER = Math.pow( 2, 8 );

// Set point batch size to 7500 defautl up to 1_920_000
const DEFAULT_POINT_BATCH_SIZE = 7500;
const MAX_POINT_BATCH_SIZE = DEFAULT_POINT_BATCH_SIZE * MAX_SIZE_MULTIPLIER;

// Set image batch size to 700 defautl up to 179_200
const DEFAULT_IMAGE_BATCH_SIZE = 700;
const MAX_IMAGE_BATCH_SIZE = DEFAULT_IMAGE_BATCH_SIZE * MAX_SIZE_MULTIPLIER;

// Set geometry batch size to 800 default up to 204_800
const DEFAULT_GEOMETRY_BATCH_SIZE = 800;
const MAX_GEOMETRY_BATCH_SIZE = DEFAULT_GEOMETRY_BATCH_SIZE * MAX_SIZE_MULTIPLIER;

// Batch will automatically shrink by 1/2 every 5 seconds if capacity is not used
const BATCH_CAPACITY_SHRINK_INTERVAL = 5000;

// String constants to identify batch system names
const BATCH_TYPES = [ "POINTS", "IMAGE", "GEOMETRY", "POINTS_REPLACE", "IMAGE_REPLACE", "SHADER" ];

// Batch prototype
const m_batchProto = {

	// Type of batch POINTS_BATCH, IMAGE_BATCH, etc...
	"type": null,

	// Tri-state: null = use default, true = alpha, false = replace
	"overrideGlobalBlend": null,

	"program": null,
	"vertices": null,
	"colors": null,
	"count": 0,

	// View origin applied to local vertices
	"originX": 0,
	"originY": 0,

	// Capacity
	"minCapacity": 0,
	"capacity": 0,
	"maxCapacity": 0,
	"capacityChanged": true,
	"capacityLocalMax": 0,
	"capacityShrinkCheckTime": 0,

	// Components
	"vertexComps": 2,
	"colorComps": 4,
	"texCoordComps": 2,

	// WebGL resources
	"vertexVBO": null,
	"colorVBO": null,
	"texCoordVBO": null,
	"vao": null,

	// Image Specific items
	"useTexture": false,
	"texture": null,

	// Drawing mode, e.g., gl.POINTS or gl.TRIANGLES
	"mode": null,

	// Cached shader locations
	"locations": null
};

const m_isDebug = window.location.search.includes( "webgl-debug" );


/**************************************************************************************************
 * Module Initialization
 ************************************************************************************************/


/**
 * Initialize batch and rendering module
 *
 * @returns {void}
 */
export function init() {

	// Initialize screenData items owned by the batch system
	g_screenManager.addScreenDataItem( "batches", {} );
	g_screenManager.addScreenDataItem( "batchInfo", {
		"currentBatch": null,
		"drawOrder": [],
		"textureBatchSet": new Set()
	} );
}

/**
 * Creates all the batches
 *
 * @param {Object} screenData - Screen state.
 * @returns {void}
 */
export function createBatches( screenData ) {

	// Create the point batch
	screenData.batches[ POINTS_BATCH ] = createBatch( screenData, POINTS_BATCH );
	screenData.batches[ IMAGE_BATCH ] = createBatch( screenData, IMAGE_BATCH );
	screenData.batches[ GEOMETRY_BATCH ] = createBatch( screenData, GEOMETRY_BATCH );
	screenData.batches[ POINTS_REPLACE_BATCH ] = createBatch( screenData, POINTS_REPLACE_BATCH );
	screenData.batches[ IMAGE_REPLACE_BATCH ] = createBatch( screenData, IMAGE_REPLACE_BATCH );

	// Shader batch: no geometry, programs created lazily per handle in customShaders
	const shaderBatch = Object.create( m_batchProto );
	shaderBatch.type = SHADER_BATCH;
	shaderBatch.overrideGlobalBlend = null;
	shaderBatch.count = 0;
	screenData.batches[ SHADER_BATCH ] = shaderBatch;
}

/**
 * Create batch system for points or images
 *
 * @param {Object} screenData - Screen data object
 * @param {number} type - Batch type (POINTS_BATCH or IMAGE_BATCH, etc..)
 * @returns {Object|null} Batch object or null on error
 */
export function createBatch( screenData, type ) {

	const gl = screenData.gl;
	const batch = Object.create( m_batchProto );
	screenData.batches[ type ] = batch;

	// Get shader sources based on batch type
	let vertSrc, fragSrc;
	if( type === POINTS_BATCH ) {
		vertSrc = m_pointVertSrc;
		fragSrc = m_pointFragSrc;
		batch.capacity = DEFAULT_POINT_BATCH_SIZE;
		batch.minCapacity = DEFAULT_POINT_BATCH_SIZE;
		batch.maxCapacity = MAX_POINT_BATCH_SIZE;
		batch.mode = gl.POINTS;
	} else if( type === IMAGE_BATCH || type === IMAGE_REPLACE_BATCH ) {
		vertSrc = m_imageVertSrc;
		fragSrc = m_imageFragSrc;
		batch.capacity = DEFAULT_IMAGE_BATCH_SIZE;
		batch.minCapacity = DEFAULT_IMAGE_BATCH_SIZE;
		batch.maxCapacity = MAX_IMAGE_BATCH_SIZE;
		batch.mode = gl.TRIANGLES;
		batch.useTexture = true;
		if( type === IMAGE_BATCH ) {
			batch.overrideGlobalBlend = true;
		}
	} else if( type === GEOMETRY_BATCH ) {
		vertSrc = m_gemoetryVertSrc;
		fragSrc = m_pointFragSrc;
		batch.capacity = DEFAULT_GEOMETRY_BATCH_SIZE;
		batch.minCapacity = DEFAULT_GEOMETRY_BATCH_SIZE;
		batch.maxCapacity = MAX_GEOMETRY_BATCH_SIZE;
		batch.mode = gl.TRIANGLES;
	} else if( type === POINTS_REPLACE_BATCH ) {
		vertSrc = m_pointVertSrc;
		fragSrc = m_pointFragSrc;
		batch.capacity = DEFAULT_POINT_BATCH_SIZE;
		batch.minCapacity = DEFAULT_POINT_BATCH_SIZE;
		batch.maxCapacity = MAX_POINT_BATCH_SIZE;
		batch.mode = gl.POINTS;
		batch.overrideGlobalBlend = false;
	} else {
		const error = new Error( `createBatch: Unknown batch type ${type}` );
		error.code = "INVALID_BATCH_TYPE";
		throw error;
	}

	// Create the batch shader program
	batch.program = g_shaders.createShaderProgram( gl, vertSrc, fragSrc );

	// Cache shader locations for efficiency
	batch.locations = {
		"position": gl.getAttribLocation( batch.program, "a_position" ),
		"color": gl.getAttribLocation( batch.program, "a_color" ),
		"resolution": gl.getUniformLocation( batch.program, "u_resolution" )
	};

	// Add noise uniform locations for batches that use point shaders
	if(
		type === POINTS_BATCH || type === POINTS_REPLACE_BATCH || type === GEOMETRY_BATCH
	) {
		batch.locations.noiseMin = gl.getUniformLocation( batch.program, "u_noiseMin" );
		batch.locations.noiseMax = gl.getUniformLocation( batch.program, "u_noiseMax" );
		batch.locations.time = gl.getUniformLocation( batch.program, "u_time" );
	}

	// Setup batch type and capacity
	batch.type = type;

	// Setup image specific webgl variables
	if( batch.useTexture === true ) {

		// Image-specific shader locations
		batch.locations.texCoord = gl.getAttribLocation( batch.program, "a_texCoord" );
		batch.locations.texture = gl.getUniformLocation( batch.program, "u_texture" );

		// Image-specific data array
		batch.texCoords = new Float32Array( batch.capacity * batch.texCoordComps );

		// Image-specific VBO
		batch.texCoordVBO = gl.createBuffer();
	}

	// These are created for all batches
	batch.vertices = new Float32Array( batch.capacity * batch.vertexComps );
	batch.colors = new Uint8Array( batch.capacity * batch.colorComps );
	batch.vertexVBO = gl.createBuffer();
	batch.colorVBO = gl.createBuffer();

	// Create VAO (WebGL2 only)
	batch.vao = gl.createVertexArray();
	if(
		!batch.vertexVBO || !batch.colorVBO || !batch.vao ||
		( batch.useTexture && !batch.texCoordVBO )
	) {
		const error = new Error( "screen: Failed to allocate batch buffers." );
		error.code = "WEBGL_ERROR";
		throw error;
	}
	gl.bindVertexArray( batch.vao );

	// Setup position attribute
	gl.bindBuffer( gl.ARRAY_BUFFER, batch.vertexVBO );
	gl.enableVertexAttribArray( batch.locations.position );
	gl.vertexAttribPointer(
		batch.locations.position, batch.vertexComps, gl.FLOAT, false, 0, 0
	);

	// Setup color attribute
	gl.bindBuffer( gl.ARRAY_BUFFER, batch.colorVBO );
	gl.enableVertexAttribArray( batch.locations.color );
	gl.vertexAttribPointer(
		batch.locations.color, batch.colorComps, gl.UNSIGNED_BYTE, true, 0, 0
	);

	// Setup texCoord attribute for image batches
	if( batch.useTexture === true ) {
		gl.bindBuffer( gl.ARRAY_BUFFER, batch.texCoordVBO );
		gl.enableVertexAttribArray( batch.locations.texCoord );
		gl.vertexAttribPointer(
			batch.locations.texCoord, batch.texCoordComps, gl.FLOAT, false, 0, 0
		);
	}

	gl.bindVertexArray( null );

	// Set the next shrink check time
	batch.capacityShrinkCheckTime = Date.now() + BATCH_CAPACITY_SHRINK_INTERVAL;

	return batch;
}

/**
 * Resize batch to new capacity
 *
 * @param {Object} batch - Batch object
 * @param {number} newCapacity - New capacity
 * @returns {void}
 */
function resizeBatch( batch, newCapacity ) {

	// Resize arrays
	const newVertices = new Float32Array( newCapacity * batch.vertexComps );
	const newColors = new Uint8Array( newCapacity * batch.colorComps );

	// Copy existing data only if there is data to copy
	// When shrinking after flush, count is 0 so no copy is needed
	if( batch.count > 0 ) {
		newVertices.set( batch.vertices.subarray( 0, batch.count * batch.vertexComps ) );
		newColors.set( batch.colors.subarray( 0, batch.count * batch.colorComps ) );
	}

	batch.vertices = newVertices;
	batch.colors = newColors;

	if( batch.useTexture === true ) {
		const newTexCoords = new Float32Array( newCapacity * batch.texCoordComps );

		// Copy existing texture coordinates only if there is data to copy
		if( batch.count > 0 ) {
			newTexCoords.set( batch.texCoords.subarray( 0, batch.count * batch.texCoordComps ) );
		}

		batch.texCoords = newTexCoords;
	}

	if( m_isDebug ) {
		console.log(
			`Batch ${BATCH_TYPES[ batch.type ]} resized from ${batch.capacity} to ${newCapacity}`
		);
	}

	// Update batch
	batch.capacity = newCapacity;
	batch.capacityChanged = true;

	// Set the time capacity last changed
	batch.capacityShrinkCheckTime = Date.now() + BATCH_CAPACITY_SHRINK_INTERVAL;
}

/**
 * Ensure batch has capacity for items. A zero-item reservation is a no-op.
 *
 * @param {Object} screenData - Screen data object
 * @param {number} batchType - Batch type
 * @param {number} itemCount - Number of items needed
 * @param {WebGLTexture} [texture] - Texture for images
 * @throws {RangeError} If itemCount is invalid or the reservation cannot fit after flushing
 * @returns {boolean} True if reserved, false if context loss canceled the reservation
 */
export function prepareBatch( screenData, batchType, itemCount, texture ) {
	if( g_contextState.isContextUnavailable( screenData ) ) {
		return false;
	}

	// Get the batch
	const batch = screenData.batches[ batchType ];
	if( !Number.isSafeInteger( itemCount ) || itemCount < 0 || itemCount > batch.maxCapacity ) {
		throw new RangeError(
			`prepareBatch: itemCount must be an integer between 0 and ${batch.maxCapacity}.`
		);
	}
	if( itemCount === 0 ) {
		return true;
	}

	let requiredCount = batch.count + itemCount;
	if( requiredCount > batch.maxCapacity ) {
		flushBatches( screenData );
		if( g_contextState.isContextUnavailable( screenData ) ) {
			return false;
		}
		requiredCount = batch.count + itemCount;
		if( requiredCount > batch.maxCapacity ) {
			throw new RangeError( "prepareBatch: Flushing could not make room for itemCount." );
		}
	}

	if( requiredCount > batch.capacity ) {
		const newCapacity = Math.max(
			requiredCount, Math.min( batch.capacity * 2, batch.maxCapacity )
		);
		resizeBatch( batch, newCapacity );
	}

	if( screenData.view ) {
		batch.originX = screenData.view.originX;
		batch.originY = screenData.view.originY;
	} else {
		batch.originX = 0;
		batch.originY = 0;
	}

	// Track if the batch type is changing or texture is changing (for image batches)
	const batchInfo = screenData.batchInfo;
	const batchTypeChanging = batchInfo.currentBatch !== batch;
	const textureChanging = batch.useTexture === true &&
		batchInfo.currentBatch === batch && batch.texture !== texture;

	if( batchTypeChanging || textureChanging ) {

		// Set the end index for the last drawOrderItem to it's current count
		if( batchInfo.drawOrder.length > 0 ) {
			const lastDrawOrderItem = batchInfo.drawOrder[ batchInfo.drawOrder.length - 1 ];
			lastDrawOrderItem.endIndex = lastDrawOrderItem.batch.count;
		}

		// Create a new draw order item
		const drawOrderItem = {
			"batch": batch,
			"startIndex": batch.count,
			"endIndex": null,
			"overrideGlobalBlend": batch.overrideGlobalBlend
		};

		// For image batches, track the current image/texture for this segment
		if( batch.useTexture === true ) {
			batch.texture = texture;
			drawOrderItem.texture = texture;
			batchInfo.textureBatchSet.add( texture );
		}

		// Add the draw order item
		batchInfo.drawOrder.push( drawOrderItem );
		batchInfo.currentBatch = batch;
	}
	return true;
}

/**
 * Reserve a bounded chunk of complete primitives for a synchronous drawing loop.
 *
 * @param {Object} screenData - Screen data object
 * @param {number} batchType - Untextured batch type
 * @param {number} [remainingCount] - Remaining vertices, omitted for unknown-length work
 * @param {number} [primitiveSize=1] - Vertices per indivisible primitive
 * @returns {number} Reserved vertex count
 */
export function prepareBatchChunk( screenData, batchType, remainingCount, primitiveSize = 1 ) {
	if( g_contextState.isContextUnavailable( screenData ) ) {
		return 0;
	}

	const batch = screenData.batches[ batchType ];
	const limit = Math.min( batch.minCapacity, batch.maxCapacity );
	if( !Number.isSafeInteger( primitiveSize ) || primitiveSize < 1 || primitiveSize > limit ) {
		throw new RangeError( "prepareBatchChunk: primitiveSize must fit in a chunk." );
	}
	if(
		remainingCount !== undefined && (
		!Number.isSafeInteger( remainingCount ) || remainingCount < 0 ||
		remainingCount % primitiveSize !== 0
	)
	) {
		throw new RangeError(
			"prepareBatchChunk: remainingCount must contain complete primitives."
		);
	}
	let count = Math.floor( limit / primitiveSize ) * primitiveSize;
	if( remainingCount !== undefined ) {
		count = Math.min( count, remainingCount );
	}

	// Use spare capacity first so short drawing loops do not cause unnecessary growth.
	const available = Math.floor( ( batch.capacity - batch.count ) / primitiveSize ) *
		primitiveSize;
	if( available > 0 ) {
		count = Math.min( count, available );
	}
	if( !prepareBatch( screenData, batchType, count ) ) {
		return 0;
	}
	return count;
}

/**
 * Queue a shader item at the current point in draw order (batch-break, does not flush).
 *
 * @param {Object} screenData - Screen data object
 * @param {{ id: number, fragmentSource: string, uniforms: Object }} handle - Shader handle
 * @param {Object} uniforms - Normalized uniform bindings
 * @param {Map} samplerTextures - Sampler source to queued WebGL texture
 * @returns {void}
 */
export function prepareShaderBatch( screenData, handle, uniforms, samplerTextures = new Map() ) {
	if( g_contextState.isContextUnavailable( screenData ) ) {
		return;
	}

	const batchInfo = screenData.batchInfo;
	const batch = screenData.batches[ SHADER_BATCH ];

	// Batch-break: close current batch before adding shader item (same as prepareBatch)
	if( batchInfo.drawOrder.length > 0 ) {
		const lastDrawOrderItem = batchInfo.drawOrder[ batchInfo.drawOrder.length - 1 ];
		lastDrawOrderItem.endIndex = lastDrawOrderItem.batch.count;
	}

	const drawOrderItem = {
		"batch": batch,
		"startIndex": 0,
		"endIndex": 0,
		"overrideGlobalBlend": batch.overrideGlobalBlend,
		"shaderHandle": handle,
		"uniforms": uniforms ?? {},
		"samplerTextures": samplerTextures
	};
	for( const texture of samplerTextures.values() ) {
		batchInfo.textureBatchSet.add( texture );
	}
	batchInfo.drawOrder.push( drawOrderItem );
	batchInfo.currentBatch = batch;
}

/**
 * Count queued passes that reference a custom shader.
 *
 * @param {Object} screenData - Screen data object
 * @param {number} shaderId - Custom shader id
 * @returns {number} Number of queued shader passes
 */
export function countQueuedShaderPasses( screenData, shaderId ) {
	let count = 0;
	for( const item of screenData.batchInfo.drawOrder ) {
		if( item.shaderHandle && item.shaderHandle.id === shaderId ) {
			count += 1;
		}
	}
	return count;
}

/**
 * Run one FBO shader pass: render FBO to bufferFBO with shader, then blit back to FBO.
 *
 * @param {Object} screenData - Screen data object
 * @param {{ batch: Object, shaderHandle: Object, uniforms: Object }} drawOrderItem
 * @returns {void}
 */
function runShaderPass( screenData, drawOrderItem ) {
	const gl = screenData.gl;
	const handle = drawOrderItem.shaderHandle;
	const uniforms = drawOrderItem.uniforms;
	const samplerTextures = drawOrderItem.samplerTextures;

	const { "program": program, "locations": locations } = g_shaders.getOrCreateCustomShaderProgram(
		screenData, handle
	);
	if( locations.texture === null ) {
		const error = new Error( "applyShader: Missing required uniform u_texture in shader." );
		error.code = "MISSING_U_TEXTURE";
		throw error;
	}

	const w = screenData.width;
	const h = screenData.height;

	// 1. Bind bufferFBO as draw target, render fullscreen quad with FBO texture as input
	gl.bindFramebuffer( gl.FRAMEBUFFER, screenData.bufferFBO );
	gl.viewport( 0, 0, w, h );
	gl.disable( gl.BLEND );
	gl.disable( gl.SCISSOR_TEST );

	gl.useProgram( program );
	gl.bindVertexArray( screenData.displayQuadVao );

	gl.activeTexture( gl.TEXTURE0 );
	gl.bindTexture( gl.TEXTURE_2D, screenData.fboTexture );
	gl.uniform1i( locations.texture, 0 );

	if( locations.sourceSize !== null ) {
		gl.uniform2f( locations.sourceSize, w, h );
	}
	if( locations.outputSize !== null ) {
		gl.uniform2f( locations.outputSize, w, h );
	}
	if( locations.time !== null ) {
		gl.uniform1f( locations.time, performance.now() / 1000 );
	}
	if( locations.frame !== null ) {
		gl.uniform1i( locations.frame, screenData.frameCount ?? 0 );
	}

	g_shaders.setCustomUniforms( gl, uniforms, ( source ) => samplerTextures.get( source ) );

	gl.drawArrays( gl.TRIANGLES, 0, 6 );

	gl.bindVertexArray( null );

	// 2. Blit bufferFBO (READ) → FBO (DRAW)
	gl.bindFramebuffer( gl.READ_FRAMEBUFFER, screenData.bufferFBO );
	gl.bindFramebuffer( gl.DRAW_FRAMEBUFFER, screenData.FBO );
	gl.blitFramebuffer( 0, 0, w, h, 0, 0, w, h, gl.COLOR_BUFFER_BIT, gl.NEAREST );

	// 3. Restore FBO as current for remaining draw order items
	gl.bindFramebuffer( gl.READ_FRAMEBUFFER, null );
	gl.bindFramebuffer( gl.DRAW_FRAMEBUFFER, screenData.FBO );
}

/**
 * Flush all batches to FBO
 *
 * @param {Object} screenData - Screen data object
 * @param {Object|null} blends - Blends data including blend mode, noise, seed or null for default
 * @param {Array<Float32Array>|null} noise - Noise values
 * @returns {void}
 */
export function flushBatches( screenData, blends = null ) {
	if( g_contextState.probeContextLoss( screenData ) ) {
		return;
	}

	// Avoid rebinding framebuffers and restoring GL state when a screen has no queued work.
	// The first flush still initializes a newly created framebuffer.
	if( !screenData.isFirstRender && screenData.batchInfo.drawOrder.length === 0 ) {
		return;
	}

	if( blends === null ) {
		blends = screenData.blends;
	}

	const gl = screenData.gl;

	if( screenData.contextLost ) {

		// TODO-LATER: Maybe add warning here?
		// console.warn( "WebGL context lost unable to render screen." );
		return;
	}

	screenData.frameCount = ( screenData.frameCount ?? 0 ) + 1;

	// Bind FBO
	gl.bindFramebuffer( gl.FRAMEBUFFER, screenData.FBO );

	// Set viewport
	gl.viewport( 0, 0, screenData.width, screenData.height );

	// Clear FBO on first render only
	if( screenData.isFirstRender ) {
		gl.clearColor( 0, 0, 0, 0 );
		gl.clear( gl.COLOR_BUFFER_BIT );
		screenData.isFirstRender = false;
	}

	// Upload batch buffers
	for( const batchType in screenData.batches ) {
		const batch = screenData.batches[ batchType ];
		if( batch.count > 0 ) {
			uploadBatch( gl, batch, screenData.width, screenData.height );
		}
	}

	// Apply the current view scissor for geometry; shader passes disable it
	applyViewScissor( gl, screenData );

	// Draw items
	for( const drawOrderItem of screenData.batchInfo.drawOrder ) {
		if( drawOrderItem.endIndex === null ) {
			drawOrderItem.endIndex = drawOrderItem.batch.count;
		}

		if( drawOrderItem.batch.type === SHADER_BATCH ) {
			runShaderPass( screenData, drawOrderItem );
			applyViewScissor( gl, screenData );
			continue;
		}

		// Only draw the batch if there is something to draw
		if( drawOrderItem.endIndex - drawOrderItem.startIndex > 0 ) {

			// Set blend mode for this item
			if( drawOrderItem.overrideGlobalBlend === null ) {

				// Other batches use global blend mode
				if( blends.blend === g_blends.BLEND_REPLACE ) {
					gl.disable( gl.BLEND );
				} else {
					gl.enable( gl.BLEND );
					gl.blendFuncSeparate(

						// premultiplied srcRGBFactor
						gl.ONE,

						// dstRGBFactor
						gl.ONE_MINUS_SRC_ALPHA,

						// srcAlphaFactor - src alpha factor 1.0 (no scale)
						gl.ONE,

						// dstAlphaFactor - dst alpha factor (1-src.a)
						gl.ONE_MINUS_SRC_ALPHA
					);
				}

			} else if( drawOrderItem.overrideGlobalBlend === true ) {

				// IMAGE_BATCH always uses alpha blending
				gl.enable( gl.BLEND );
				gl.blendFuncSeparate(

					// premultiplied srcRGBFactor
					gl.ONE,

					// dstRGBFactor
					gl.ONE_MINUS_SRC_ALPHA,

					// srcAlphaFactor - src alpha factor 1.0 (no scale)
					gl.ONE,

					// dstAlphaFactor - dst alpha factor (1-src.a)
					gl.ONE_MINUS_SRC_ALPHA
				);
			} else {
				gl.disable( gl.BLEND );
			}

			let texture = null;
			if( drawOrderItem.batch.useTexture === true ) {
				texture = drawOrderItem.texture;
			}
			drawBatch(
				gl, screenData, drawOrderItem.batch,
				drawOrderItem.startIndex, drawOrderItem.endIndex, texture, blends
			);
		}
	}

	// Reset Batches
	for( const batchType in screenData.batches ) {
		const batch = screenData.batches[ batchType ];
		resetBatch( batch );
	}

	// Reset drawOrder object
	screenData.batchInfo.drawOrder = [];
	screenData.batchInfo.currentBatch = null;
	screenData.batchInfo.textureBatchSet.clear();

	// Unbind VAO
	gl.bindVertexArray( null );

	gl.disable( gl.SCISSOR_TEST );

	// Unbind FBO
	gl.bindFramebuffer( gl.FRAMEBUFFER, null );
}

/**
 * Enable scissor for the current view clip, or disable if the clip is full-screen.
 *
 * @param {WebGL2RenderingContext} gl - WebGL2 context
 * @param {Object} screenData - Screen data object
 * @returns {void}
 */
function applyViewScissor( gl, screenData ) {
	const view = screenData.view;
	if( !view || view.clipWidth <= 0 || view.clipHeight <= 0 ) {
		gl.enable( gl.SCISSOR_TEST );
		gl.scissor( 0, 0, 0, 0 );
		return;
	}

	if(
		view.clipX === 0 && view.clipY === 0 &&
		view.clipWidth === screenData.width &&
		view.clipHeight === screenData.height
	) {
		gl.disable( gl.SCISSOR_TEST );
		return;
	}

	gl.enable( gl.SCISSOR_TEST );
	const scissorY = screenData.height - ( view.clipY + view.clipHeight );
	gl.scissor( view.clipX, scissorY, view.clipWidth, view.clipHeight );
}

/**
 * Upload batch data to GPU
 *
 * @param {WebGL2RenderingContext} gl - WebGL2 context
 * @param {Object} batch - Batch object
 * @param {number} width - Screen width
 * @param {number} height - Screen height
 * @returns {void}
 */
function uploadBatch( gl, batch, width, height ) {
	gl.useProgram( batch.program );
	gl.uniform2f( batch.locations.resolution, width, height );
	gl.bindVertexArray( batch.vao );

	// Allocate or resize buffers on capacity change
	if( batch.capacityChanged ) {
		gl.bindBuffer( gl.ARRAY_BUFFER, batch.vertexVBO );
		gl.bufferData( gl.ARRAY_BUFFER, batch.vertices.byteLength, gl.STREAM_DRAW );
		gl.bindBuffer( gl.ARRAY_BUFFER, batch.colorVBO );
		gl.bufferData( gl.ARRAY_BUFFER, batch.colors.byteLength, gl.STREAM_DRAW );

		if( batch.useTexture === true ) {
			gl.bindBuffer( gl.ARRAY_BUFFER, batch.texCoordVBO );
			gl.bufferData( gl.ARRAY_BUFFER, batch.texCoords.byteLength, gl.STREAM_DRAW );
		}

		batch.capacityChanged = false;
	}

	// Upload positions
	gl.bindBuffer( gl.ARRAY_BUFFER, batch.vertexVBO );
	gl.bufferSubData(
		gl.ARRAY_BUFFER, 0, batch.vertices.subarray( 0, batch.count * batch.vertexComps )
	);

	// Upload colors
	gl.bindBuffer( gl.ARRAY_BUFFER, batch.colorVBO );
	gl.bufferSubData(
		gl.ARRAY_BUFFER, 0, batch.colors.subarray( 0, batch.count * batch.colorComps )
	);

	// Upload texture coordinates
	if( batch.useTexture === true ) {
		gl.bindBuffer( gl.ARRAY_BUFFER, batch.texCoordVBO );
		gl.bufferSubData(
			gl.ARRAY_BUFFER, 0, batch.texCoords.subarray( 0, batch.count * batch.texCoordComps )
		);
	}
}

/**
 * Draw batch to FBO
 *
 * @param {WebGL2RenderingContext} gl - WebGL2 context
 * @param {Object} screenData - Screen data object
 * @param {Object} batch - Batch object
 * @param {number} startIndex - Start index in batch
 * @param {number} endIndex - End index in batch
 * @param {WebGLTexture|null} texture - Texture for image batches or null
 * @param {Object} blends - Blends data including blend mode, noise, seed or null default
 * @returns {void}
 */
function drawBatch( gl, screenData, batch, startIndex, endIndex, texture = null, blends = null ) {

	gl.useProgram( batch.program );
	gl.bindVertexArray( batch.vao );

	// For image batches, bind the texture and set uniform
	if( batch.useTexture === true && texture ) {
		gl.activeTexture( gl.TEXTURE0 );
		gl.bindTexture( gl.TEXTURE_2D, texture );
		gl.uniform1i( batch.locations.texture, 0 );
	}

	// Set noise uniforms for batches that use point shaders
	if( batch.locations.noiseMin !== undefined ) {
		if( blends === null ) {
			blends = screenData.blends;
		}
		const noise = blends.noise;
		const noiseSeed = blends.noiseSeed;

		// Calculate noise min and max ranges
		let noiseMin, noiseMax;
		if( noise === null ) {

			// No noise - set both to zero
			noiseMin = new Float32Array( [ 0.0, 0.0, 0.0, 0.0 ] );
			noiseMax = new Float32Array( [ 0.0, 0.0, 0.0, 0.0 ] );
		} else {

			noiseMin = noise[ 0 ];
			noiseMax = noise[ 1 ];
		}

		gl.uniform4fv( batch.locations.noiseMin, noiseMin );
		gl.uniform4fv( batch.locations.noiseMax, noiseMax );

		// Set time for animated noise
		// Use seed if available, otherwise use current time
		let timeValue;
		if( noiseSeed !== null ) {
			timeValue = noiseSeed / 1000.0;
		} else {
			timeValue = performance.now() / 1000.0;
		}
		gl.uniform1f( batch.locations.time, timeValue );
	}

	// Draw based on batch mode
	gl.drawArrays( batch.mode, startIndex, endIndex - startIndex );
}


/**
 * Reset batches and draw order items
 *
 * @param {Object} batch - Batch object
 * @param {Object} screenData - Screen state.
 * @returns {void}
 */
export function resetBatches( screenData ) {

	// Reset Batches
	for( const batchType in screenData.batches ) {
		const batch = screenData.batches[ batchType ];
		resetBatch( batch );
	}

	// Reset drawOrder object
	screenData.batchInfo.drawOrder = [];
	screenData.batchInfo.currentBatch = null;
	screenData.batchInfo.textureBatchSet.clear();
}

/**
 * Reset batch after flush
 *
 * @param {Object} batch - Batch object
 * @returns {void}
 */
function resetBatch( batch ) {

	if( batch.type === SHADER_BATCH ) {
		batch.count = 0;
		return;
	}

	// Update the batch local max
	batch.capacityLocalMax = Math.max( batch.count, batch.capacityLocalMax );

	// Reset batch count
	batch.count = 0;

	if( batch.useTexture === true ) {
		batch.texture = null;
		batch.image = null;
	}

	// Check if should shrink capacity
	if( Date.now() > batch.capacityShrinkCheckTime ) {

		// This will resize the batch slowly over time - cutting in half every 5 seconds
		if( batch.capacity > batch.minCapacity && batch.capacityLocalMax < batch.capacity * 0.5 ) {

			// Resize the batch
			resizeBatch( batch, Math.max( batch.capacity * 0.5, batch.minCapacity ) );
		}

		batch.capacityShrinkCheckTime = Date.now() + BATCH_CAPACITY_SHRINK_INTERVAL;
		batch.capacityLocalMax = 0;
	}
}

/**
 * Display FBO texture to canvas
 *
 * @param {Object} screenData - Screen data object
 * @returns {void}
 */
export function displayToCanvas( screenData ) {
	if( g_contextState.probeContextLoss( screenData ) ) {
		return;
	}

	// Parent-affiliated offscreen screens share the visible screen's context but remain FBO-only.
	if( screenData.isOffscreen && screenData.parentRenderContext ) {
		return;
	}

	const gl = screenData.gl;
	const useCustom = !screenData.isOffscreen && !!screenData.displayShaderHandle;

	let program;
	let locations;
	let customUniforms = null;

	if( useCustom ) {
		const handle = screenData.displayShaderHandle;
		const cache = g_shaders.getOrCreateCustomShaderProgram( screenData, handle );
		if( cache.locations.texture === null ) {
			const error = new Error(
				"setDisplayShader: Missing required uniform u_texture in shader."
			);
			error.code = "MISSING_U_TEXTURE";
			throw error;
		}
		program = cache.program;
		locations = cache.locations;
		customUniforms = screenData.displayShaderUniformBindings ?? {};
	} else {
		program = screenData.displayProgram;
		locations = screenData.displayLocations;
	}
	const samplerTextures = new Map();
	if( useCustom ) {
		for( const binding of Object.values( customUniforms ) ) {
			if( binding.info.family !== "sampler" ) {
				continue;
			}
			for( const source of binding.sources ) {
				if( !samplerTextures.has( source ) ) {
					samplerTextures.set(
						source, screenData.displayShaderTextureResolver( source )
					);
				}
			}
		}
	}

	// Bind default framebuffer (screen)
	gl.bindFramebuffer( gl.FRAMEBUFFER, null );

	// Viewport always matches the actual canvas backing store
	gl.viewport( 0, 0, screenData.canvas.width, screenData.canvas.height );

	// Clear the canvas before drawing the FBO texture
	gl.clearColor( 0, 0, 0, 0 );
	gl.clear( gl.COLOR_BUFFER_BIT );

	gl.disable( gl.BLEND );
	gl.disable( gl.SCISSOR_TEST );

	gl.useProgram( program );
	gl.bindVertexArray( screenData.displayQuadVao );

	gl.activeTexture( gl.TEXTURE0 );
	gl.bindTexture( gl.TEXTURE_2D, screenData.fboTexture );
	gl.uniform1i( locations.texture, 0 );

	if( useCustom ) {
		if( locations.sourceSize !== null ) {
			gl.uniform2f( locations.sourceSize, screenData.width, screenData.height );
		}
		if( locations.outputSize !== null ) {
			gl.uniform2f(
				locations.outputSize, screenData.canvas.width, screenData.canvas.height
			);
		}
		if( locations.time !== null ) {
			gl.uniform1f( locations.time, performance.now() / 1000 );
		}
		if( locations.frame !== null ) {
			gl.uniform1i( locations.frame, screenData.frameCount ?? 0 );
		}
		g_shaders.setCustomUniforms(
			gl, customUniforms, ( source ) => samplerTextures.get( source )
		);
	}

	gl.drawArrays( gl.TRIANGLES, 0, 6 );

	gl.bindVertexArray( null );
}

/**
 * Cleanup batch resources for screen
 *
 * @param {Object} screenData - Screen data object
 * @returns {void}
 */
export function cleanup( screenData ) {
	const gl = screenData.gl;

	// Cleanup batches
	for( const batchType in screenData.batches ) {

		// Get the batch
		const batch = screenData.batches[ batchType ];

		if( batch.type === SHADER_BATCH ) {
			continue;
		}

		// Delete texCoord items
		if( batch.texCoordVBO ) {
			gl.deleteBuffer( batch.texCoordVBO );
		}

		gl.deleteBuffer( batch.vertexVBO );
		gl.deleteBuffer( batch.colorVBO );
		gl.deleteVertexArray( batch.vao );
		gl.deleteProgram( batch.program );

		if( batch.useTexture === true ) {
			batch.texture = null;
			batch.image = null;
		}
	}

	// Clear references for GC and signal uninitialized state
	screenData.batches = null;
	screenData.batchInfo = null;
}
